/**
 * Row → DocumentModel adapters for quotes and invoices.
 *
 * Rules that apply to every document built here:
 * - Internal notes (`notes`) are staff-only and are never placed on a document.
 *   The create forms label them "not shown to customer"; keep that promise.
 * - Tax is resolved from the per-row snapshot first so a historical document
 *   keeps the rate it was issued under even if the company changes settings.
 * - Money and dates go through the tenant locale so a non-Australian tenant
 *   sees their own currency symbol, identifier labels and date order.
 */
import type { Company } from '@/types';
import type { EstimateWithRelations } from '@/types/estimate';
import type { InvoiceWithRelations } from '@/types/invoice';
import type { TaxConfig } from '@/utils/tax';
import { formatTaxLine, taxInvoiceHeading } from '@/utils/tax';
import {
  formatMoney,
  formatDocDate,
  isDateOnlyPast,
  DEFAULT_COMPANY_LOCALE,
  type CompanyLocale,
} from '@/utils/locale';
import { DEFAULT_TIMEZONE } from '@/utils/timezone';
import { paymentBreakdown, type BreakdownPayment, type PaymentBreakdown } from '@/utils/paymentBreakdown';
import type {
  DocumentCompany,
  DocumentField,
  DocumentLineItem,
  DocumentModel,
  DocumentSection,
  DocumentTone,
  DocumentTotals,
} from './types';

export const FALLBACK_PRIMARY = '#2f58c8';

/** Subset of the company profile a document needs. Optional so the public signing page can pass partial data. */
export type DocumentCompanyInput = Partial<Pick<Company,
  | 'name' | 'legal_name' | 'trading_name' | 'logo_url' | 'street_address' | 'suburb' | 'state' | 'postcode'
  | 'phone' | 'email' | 'website' | 'abn' | 'acn' | 'primary_color'
  | 'bank_account_name' | 'bsb' | 'account_number' | 'payment_instructions'
  | 'invoice_terms' | 'warranty_text' | 'country'
>> | null | undefined;

interface BuildContext {
  company: DocumentCompanyInput;
  tax: TaxConfig;
  locale?: CompanyLocale;
  timezone?: string;
  /** Invoices: signed URL of the SVG in the private job-signatures bucket (resolved by the caller). */
  signatureImageUrl?: string | null;
  /**
   * Invoices: every payment on the invoice's job. invoices.amount_paid misses
   * payments added on the job after the invoice was raised, so the ledger wins.
   */
  payments?: BreakdownPayment[] | null;
}

/** Totals fields for the "Total price / Deposit paid / Partial payment / Balance due" block. */
function paymentTotals(b: PaymentBreakdown, formatDate: (v: string) => string): Pick<DocumentTotals, 'totalLabel' | 'paid' | 'payments' | 'balanceDue'> {
  return {
    totalLabel: b.totalLabel,
    paid: b.paid > 0 ? b.paid : undefined,
    payments: b.rows.map((r) => ({ label: r.label, detail: r.date ? formatDate(r.date) : null, amount: r.amount })),
    balanceDue: b.balance,
  };
}

const clean = (v: string | null | undefined): string => (v ?? '').trim();

/** Collapse a free-text address into printable lines (split on newlines, drop blanks). */
export function addressLines(address: string | null | undefined): string[] {
  return clean(address).split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
}

export function buildDocumentCompany(company: DocumentCompanyInput, locale: CompanyLocale): DocumentCompany {
  const displayName = clean(company?.trading_name) || clean(company?.name) || 'Company Name';
  const legal = clean(company?.legal_name);
  const cityLine = [company?.suburb, company?.state, company?.postcode].map(clean).filter(Boolean).join(' ');
  const identifiers: DocumentField[] = [];
  if (clean(company?.abn)) identifiers.push({ label: locale.taxIdLabels.abn, value: clean(company?.abn) });
  if (clean(company?.acn)) identifiers.push({ label: locale.taxIdLabels.acn, value: clean(company?.acn) });

  return {
    displayName,
    legalName: legal && legal !== displayName ? legal : null,
    logoUrl: clean(company?.logo_url) || null,
    addressLines: [...addressLines(company?.street_address), cityLine].filter(Boolean),
    phone: clean(company?.phone) || null,
    email: clean(company?.email) || null,
    website: clean(company?.website) || null,
    identifiers,
    primaryColor: /^#[0-9a-f]{6}$/i.test(clean(company?.primary_color)) ? clean(company?.primary_color) : FALLBACK_PRIMARY,
  };
}

function legalLine(company: DocumentCompany): string {
  return [
    company.legalName ?? company.displayName,
    ...company.identifiers.map((i) => `${i.label} ${i.value}`),
    company.website,
  ].filter(Boolean).join('  ·  ');
}

function toLineItems(items: Array<{ description: string; quantity: number; unit_price: number; amount: number }> | null | undefined): DocumentLineItem[] {
  return (items ?? []).map((i) => ({
    description: clean(i.description),
    quantity: Number(i.quantity) || 0,
    unitPrice: Number(i.unit_price) || 0,
    amount: Number(i.amount) || 0,
  }));
}

function taxNote(tax: TaxConfig): string | undefined {
  if (!tax.enabled || tax.rate === 0) return undefined;
  return `Line items are shown exclusive of ${tax.label}. ${formatTaxLine(tax)} is added in the totals above.`;
}

function companySections(company: DocumentCompanyInput, opts: { includeWarranty: boolean }): DocumentSection[] {
  const sections: DocumentSection[] = [];
  if (opts.includeWarranty && clean(company?.warranty_text)) {
    sections.push({ heading: 'Warranty', body: clean(company?.warranty_text) });
  }
  return sections;
}

// ---------------------------------------------------------------------------
// Estimates
// ---------------------------------------------------------------------------

const ESTIMATE_STATUS: Record<string, { label: string; tone: DocumentTone; stamp?: string }> = {
  draft: { label: 'Draft', tone: 'neutral', stamp: 'DRAFT' },
  sent: { label: 'Awaiting acceptance', tone: 'info' },
  viewed: { label: 'Viewed', tone: 'info' },
  accepted: { label: 'Accepted', tone: 'success', stamp: 'ACCEPTED' },
  declined: { label: 'Declined', tone: 'danger', stamp: 'DECLINED' },
  expired: { label: 'Expired', tone: 'warning', stamp: 'EXPIRED' },
  cancelled: { label: 'Cancelled', tone: 'neutral', stamp: 'CANCELLED' },
};

export function buildEstimateDocument(estimate: EstimateWithRelations, ctx: BuildContext): DocumentModel {
  const locale = ctx.locale ?? DEFAULT_COMPANY_LOCALE;
  const tz = ctx.timezone ?? DEFAULT_TIMEZONE;
  const company = buildDocumentCompany(ctx.company, locale);
  const money = (n: number) => formatMoney(n, locale);
  const long = (v: string | null | undefined) => formatDocDate(v, locale.longDate, tz);

  const expiredByDate = isDateOnlyPast(estimate.valid_until) && !['accepted', 'declined', 'cancelled'].includes(estimate.status);
  const statusKey = expiredByDate ? 'expired' : estimate.status;
  const status = ESTIMATE_STATUS[statusKey] ?? ESTIMATE_STATUS.draft;

  const meta: DocumentField[] = [
    { label: 'Quote date', value: long(estimate.created_at) },
    { label: 'Valid until', value: estimate.valid_until ? long(estimate.valid_until) : 'On request' },
  ];
  if (estimate.lead?.lead_number) meta.push({ label: 'Reference', value: estimate.lead.lead_number });
  if (estimate.customer?.customer_number) meta.push({ label: 'Customer no.', value: estimate.customer.customer_number });
  if (estimate.creator?.full_name) meta.push({ label: 'Prepared by', value: estimate.creator.full_name });

  const signed = estimate.status === 'accepted' && (!!estimate.signer_name || !!estimate.signature_image_url);
  const acceptedLabel = estimate.signed_at ?? estimate.accepted_at;
  const auditBits = [
    estimate.signer_ip_address ? `IP ${estimate.signer_ip_address}` : null,
    estimate.signature_consent ? 'Electronic-signature consent recorded' : null,
  ].filter(Boolean);

  const sections: DocumentSection[] = [];
  if (clean(estimate.terms)) sections.push({ heading: 'Terms & conditions', body: clean(estimate.terms) });
  sections.push(...companySections(ctx.company, { includeWarranty: true }));

  return {
    kind: 'estimate',
    title: 'Quotation',
    number: estimate.estimate_number,
    status: { label: status.label, tone: status.tone },
    stamp: status.stamp ? { label: status.stamp, tone: status.tone } : null,
    company,
    meta,
    billTo: {
      heading: 'Prepared for',
      name: clean(estimate.customer_name) || clean(estimate.customer?.customer_name) || 'Customer',
      lines: [
        ...(estimate.customer?.company_name ? [estimate.customer.company_name] : []),
        ...addressLines(estimate.customer_address),
      ],
      phone: estimate.customer_phone,
      email: estimate.sent_to_email,
    },
    serviceAddress: null,
    scopeHeading: 'Scope of work',
    scope: clean(estimate.service_description) || null,
    lineItems: toLineItems(estimate.line_items),
    totals: {
      subtotal: Number(estimate.subtotal) || 0,
      tax: ctx.tax.enabled ? { label: formatTaxLine(ctx.tax), amount: Number(estimate.gst_amount) || 0 } : null,
      total: Number(estimate.estimate_amount) || 0,
      note: taxNote(ctx.tax),
    },
    payment: null,
    acceptance: {
      signed,
      signerName: estimate.signer_name,
      signedAtLabel: acceptedLabel ? formatDocDate(acceptedLabel, `${locale.longDate} 'at' h:mm a`, tz) : null,
      signatureImageUrl: estimate.signature_image_url,
      auditLine: auditBits.length ? auditBits.join(' · ') : null,
      howToAccept: estimate.status === 'declined'
        ? null
        : `To accept this quotation, sign it online using the link in your email, or contact ${company.displayName} on ${company.phone ?? company.email ?? 'the details above'}.`,
    },
    sections,
    footer: {
      thanks: 'Thank you for the opportunity to quote on this work.',
      legalLine: legalLine(company),
      currencyLine: `All amounts in ${locale.currency}`,
    },
    formatMoney: money,
    paper: locale.paper,
  };
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

const INVOICE_STATUS: Record<string, { label: string; tone: DocumentTone; stamp?: string }> = {
  draft: { label: 'Draft', tone: 'neutral', stamp: 'DRAFT' },
  sent: { label: 'Awaiting payment', tone: 'info' },
  paid: { label: 'Paid', tone: 'success', stamp: 'PAID' },
  overdue: { label: 'Overdue', tone: 'danger', stamp: 'OVERDUE' },
  cancelled: { label: 'Cancelled', tone: 'neutral', stamp: 'CANCELLED' },
  refunded: { label: 'Refunded', tone: 'neutral', stamp: 'REFUNDED' },
};

export function buildInvoiceDocument(invoice: InvoiceWithRelations, ctx: BuildContext): DocumentModel {
  const locale = ctx.locale ?? DEFAULT_COMPANY_LOCALE;
  const tz = ctx.timezone ?? DEFAULT_TIMEZONE;
  const company = buildDocumentCompany(ctx.company, locale);
  const money = (n: number) => formatMoney(n, locale);
  const long = (v: string | null | undefined) => formatDocDate(v, locale.longDate, tz);

  const status = INVOICE_STATUS[invoice.status] ?? INVOICE_STATUS.draft;
  const total = Number(invoice.total_amount) || 0;
  // Paid = the larger of the invoice row and the job's payment ledger, rounded
  // to cents (money columns carry float noise, so 0.005 never prints as 0.01).
  const breakdown = paymentBreakdown(total, {
    invoicePaid: invoice.amount_paid,
    payments: ctx.payments,
    settled: invoice.status === 'paid' || invoice.status === 'refunded',
  });
  const balance = breakdown.balance;

  const meta: DocumentField[] = [
    { label: 'Issue date', value: long(invoice.issue_date) },
    { label: invoice.status === 'paid' ? 'Paid on' : 'Due date', value: invoice.status === 'paid' && invoice.paid_date ? long(invoice.paid_date) : long(invoice.due_date) },
  ];
  if (invoice.job?.job_number) meta.push({ label: 'Job reference', value: invoice.job.job_number });
  const serviceDate = invoice.job?.job_date ?? invoice.job?.scheduled_date;
  if (serviceDate) meta.push({ label: 'Service date', value: long(serviceDate) });
  if (invoice.customer?.customer_number) meta.push({ label: 'Customer no.', value: invoice.customer.customer_number });
  if (invoice.creator?.full_name) meta.push({ label: 'Prepared by', value: invoice.creator.full_name });

  const bank: DocumentField[] = [];
  if (clean(ctx.company?.bank_account_name)) bank.push({ label: locale.bankLabels.bank_account_name, value: clean(ctx.company?.bank_account_name) });
  if (clean(ctx.company?.bsb)) bank.push({ label: locale.bankLabels.bsb, value: clean(ctx.company?.bsb) });
  if (clean(ctx.company?.account_number)) bank.push({ label: locale.bankLabels.account_number, value: clean(ctx.company?.account_number) });
  const instructions = clean(ctx.company?.payment_instructions) || null;
  const onlineUrl = clean(invoice.payment_url) || null;
  const showPayment = balance > 0 && invoice.status !== 'cancelled' && (bank.length > 0 || !!instructions || !!onlineUrl);

  const sections: DocumentSection[] = [];
  const terms = clean(invoice.terms) || clean(ctx.company?.invoice_terms);
  if (terms) sections.push({ heading: 'Payment terms', body: terms });
  sections.push(...companySections(ctx.company, { includeWarranty: true }));

  const serviceAddress = clean(invoice.service_address);
  const billingAddress = clean(invoice.customer_address);

  return {
    kind: 'invoice',
    title: taxInvoiceHeading(ctx.tax),
    number: invoice.invoice_number,
    status: { label: status.label, tone: status.tone },
    stamp: status.stamp ? { label: status.stamp, tone: status.tone } : null,
    company,
    meta,
    billTo: {
      heading: 'Bill to',
      name: clean(invoice.customer_name) || clean(invoice.customer?.customer_name) || 'Customer',
      lines: [
        ...(invoice.customer?.company_name ? [invoice.customer.company_name] : []),
        ...addressLines(billingAddress),
      ],
      phone: invoice.customer_phone,
      email: invoice.customer_email,
    },
    // Only repeat the address when it adds information
    serviceAddress: serviceAddress && serviceAddress !== billingAddress ? serviceAddress : null,
    scopeHeading: 'Work performed',
    scope: clean(invoice.job?.work_summary) || null,
    lineItems: toLineItems(invoice.line_items),
    totals: {
      subtotal: Number(invoice.subtotal) || 0,
      tax: ctx.tax.enabled ? { label: formatTaxLine(ctx.tax), amount: Number(invoice.gst_amount) || 0 } : null,
      total,
      ...paymentTotals(breakdown, (v) => formatDocDate(v, locale.shortDate, tz)),
      note: taxNote(ctx.tax),
    },
    payment: showPayment
      ? {
          heading: 'How to pay',
          fields: bank,
          instructions,
          referenceHint: `Please quote ${invoice.invoice_number} as your payment reference.`,
          onlineUrl,
          onlineLabel: 'Pay online by card',
        }
      : null,
    // Signature captured on the technician's device when the job was closed from the app.
    acceptance: invoice.signed_at
      ? {
          heading: 'Signed',
          signed: true,
          signerName: invoice.signer_name,
          signedAtLabel: formatDocDate(invoice.signed_at, `${locale.longDate} 'at' h:mm a`, tz),
          signatureImageUrl: ctx.signatureImageUrl ?? null,
          auditLine: [
            'Signed on the technician\'s device',
            invoice.signature_consent ? 'customer confirmed the work and agreed to this invoice' : null,
          ].filter(Boolean).join(' · '),
          howToAccept: null,
        }
      : null,
    sections,
    footer: {
      thanks: invoice.status === 'paid' ? 'Thank you, this invoice has been paid in full.' : 'Thank you for your business.',
      legalLine: legalLine(company),
      currencyLine: `All amounts in ${locale.currency}`,
    },
    formatMoney: money,
    paper: locale.paper,
  };
}

// ---------------------------------------------------------------------------
// Public signing page (data comes from get-estimate-by-token, camelCase)
// ---------------------------------------------------------------------------

/** Company profile as exposed to customers (no bank details, no secrets). */
export interface PublicCompany {
  name: string;
  legal_name?: string | null;
  trading_name?: string | null;
  logo_url: string | null;
  phone: string | null;
  email: string | null;
  website?: string | null;
  primary_color: string | null;
  abn: string | null;
  acn?: string | null;
  country?: string | null;
  street_address?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  /** Invoice extras (portal only) */
  payment_instructions?: string | null;
  invoice_terms?: string | null;
  warranty_text?: string | null;
}

export interface PublicTax {
  rate: number | null;
  label: string | null;
  inclusive: boolean | null;
}

export interface PublicEstimatePayload {
  estimateNumber: string;
  customerName: string;
  customerAddress: string | null;
  serviceDescription: string | null;
  lineItems: Array<{ description: string; quantity: number; unit_price: number; amount: number }>;
  subtotal: number;
  gstAmount: number;
  estimateAmount: number;
  validUntil: string | null;
  terms: string | null;
  createdAt?: string | null;
  tax?: PublicTax;
  company: PublicCompany;
}

export interface PublicDocumentOptions {
  /** Row status; when given the document carries the same badge + stamp as the office copy. */
  status?: string;
  /** Company timezone for rendering timestamps (created_at, signed_at). */
  timezone?: string;
}

function publicTaxConfig(tax: PublicTax | undefined): TaxConfig {
  const rate = tax?.rate ?? 0;
  return {
    enabled: !!tax?.label && rate > 0,
    rate,
    label: tax?.label ?? 'Tax',
    inclusive: tax?.inclusive ?? true,
  };
}

function publicCompanyInput(company: PublicCompany): DocumentCompanyInput {
  return {
    name: company.name,
    legal_name: company.legal_name ?? null,
    trading_name: company.trading_name ?? null,
    logo_url: company.logo_url,
    phone: company.phone,
    email: company.email,
    website: company.website ?? null,
    primary_color: company.primary_color,
    abn: company.abn,
    acn: company.acn ?? null,
    street_address: company.street_address ?? null,
    suburb: company.suburb ?? null,
    state: company.state ?? null,
    postcode: company.postcode ?? null,
    payment_instructions: company.payment_instructions ?? null,
    invoice_terms: company.invoice_terms ?? null,
    warranty_text: company.warranty_text ?? null,
  };
}

export function buildPublicEstimateDocument(
  data: PublicEstimatePayload,
  locale: CompanyLocale,
  opts: PublicDocumentOptions = {},
): DocumentModel {
  const tax = publicTaxConfig(data.tax);
  const company = buildDocumentCompany(publicCompanyInput(data.company), locale);
  const long = (v: string | null | undefined) => formatDocDate(v, locale.longDate, opts.timezone);

  const meta: DocumentField[] = [];
  if (data.createdAt) meta.push({ label: 'Quote date', value: long(data.createdAt) });
  meta.push({ label: 'Valid until', value: data.validUntil ? long(data.validUntil) : 'On request' });

  const sections: DocumentSection[] = [];
  if (clean(data.terms)) sections.push({ heading: 'Terms & conditions', body: clean(data.terms) });

  const status = opts.status ? ESTIMATE_STATUS[opts.status] : undefined;

  return {
    kind: 'estimate',
    title: 'Quotation',
    number: data.estimateNumber,
    status: status ? { label: status.label, tone: status.tone } : null,
    stamp: status?.stamp ? { label: status.stamp, tone: status.tone } : null,
    company,
    meta,
    billTo: {
      heading: 'Prepared for',
      name: clean(data.customerName) || 'Customer',
      lines: addressLines(data.customerAddress),
    },
    scopeHeading: 'Scope of work',
    scope: clean(data.serviceDescription) || null,
    lineItems: toLineItems(data.lineItems),
    totals: {
      subtotal: Number(data.subtotal) || 0,
      tax: tax.enabled ? { label: formatTaxLine(tax), amount: Number(data.gstAmount) || 0 } : null,
      total: Number(data.estimateAmount) || 0,
      note: taxNote(tax),
    },
    payment: null,
    acceptance: null,
    sections,
    footer: {
      thanks: 'Thank you for the opportunity to quote on this work.',
      legalLine: legalLine(company),
      currencyLine: `All amounts in ${locale.currency}`,
    },
    formatMoney: (n) => formatMoney(n, locale),
    paper: locale.paper,
  };
}

// ---------------------------------------------------------------------------
// Customer portal invoice (data comes from the portal_invoices view)
// ---------------------------------------------------------------------------

export interface PublicInvoicePayload {
  invoiceNumber: string;
  status: string;
  issueDate: string | null;
  dueDate: string | null;
  paidDate: string | null;
  customerName: string;
  customerAddress: string | null;
  serviceAddress: string | null;
  lineItems: Array<{ description: string; quantity: number; unit_price: number; amount: number }>;
  subtotal: number;
  gstAmount: number;
  totalAmount: number;
  amountPaid: number;
  amountOutstanding: number | null;
  terms: string | null;
  /** Hosted pay-by-card link; null when the invoice is not payable online */
  paymentUrl: string | null;
  jobNumber?: string | null;
  serviceDate?: string | null;
  workSummary?: string | null;
  tax?: PublicTax;
  company: PublicCompany;
}

/**
 * Mirrors `buildInvoiceDocument` minus everything a customer should not see:
 * customer number, preparer, technician, customer phone/email, internal notes,
 * bank details (not exposed by the portal view).
 */
export function buildPublicInvoiceDocument(
  data: PublicInvoicePayload,
  locale: CompanyLocale,
  opts: PublicDocumentOptions = {},
): DocumentModel {
  const tax = publicTaxConfig(data.tax);
  const company = buildDocumentCompany(publicCompanyInput(data.company), locale);
  const long = (v: string | null | undefined) => formatDocDate(v, locale.longDate, opts.timezone);

  const status = INVOICE_STATUS[data.status] ?? INVOICE_STATUS.sent;
  const total = Number(data.totalAmount) || 0;
  // The portal view carries no payment ledger, so this is the invoice row's amount_paid.
  const breakdown = paymentBreakdown(total, {
    invoicePaid: data.amountPaid,
    settled: data.status === 'paid' || data.status === 'refunded',
  });
  const balance = breakdown.balance;

  const meta: DocumentField[] = [
    { label: 'Issue date', value: long(data.issueDate) },
    { label: data.status === 'paid' ? 'Paid on' : 'Due date', value: data.status === 'paid' && data.paidDate ? long(data.paidDate) : long(data.dueDate) },
  ];
  if (data.jobNumber) meta.push({ label: 'Job reference', value: data.jobNumber });
  if (data.serviceDate) meta.push({ label: 'Service date', value: long(data.serviceDate) });

  const instructions = clean(data.company.payment_instructions) || null;
  const onlineUrl = clean(data.paymentUrl) || null;
  const showPayment = balance > 0 && data.status !== 'cancelled' && (!!instructions || !!onlineUrl);

  const sections: DocumentSection[] = [];
  const terms = clean(data.terms) || clean(data.company.invoice_terms);
  if (terms) sections.push({ heading: 'Payment terms', body: terms });
  sections.push(...companySections(publicCompanyInput(data.company), { includeWarranty: true }));

  const serviceAddress = clean(data.serviceAddress);
  const billingAddress = clean(data.customerAddress);

  return {
    kind: 'invoice',
    title: taxInvoiceHeading(tax),
    number: data.invoiceNumber,
    status: { label: status.label, tone: status.tone },
    stamp: status.stamp ? { label: status.stamp, tone: status.tone } : null,
    company,
    meta,
    billTo: {
      heading: 'Bill to',
      name: clean(data.customerName) || 'Customer',
      lines: addressLines(billingAddress),
    },
    serviceAddress: serviceAddress && serviceAddress !== billingAddress ? serviceAddress : null,
    scopeHeading: 'Work performed',
    scope: clean(data.workSummary) || null,
    lineItems: toLineItems(data.lineItems),
    totals: {
      subtotal: Number(data.subtotal) || 0,
      tax: tax.enabled ? { label: formatTaxLine(tax), amount: Number(data.gstAmount) || 0 } : null,
      total,
      ...paymentTotals(breakdown, (v) => formatDocDate(v, locale.shortDate, opts.timezone)),
      note: taxNote(tax),
    },
    payment: showPayment
      ? {
          heading: 'How to pay',
          fields: [],
          instructions,
          referenceHint: `Please quote ${data.invoiceNumber} as your payment reference.`,
          onlineUrl,
          onlineLabel: 'Pay online by card',
        }
      : null,
    acceptance: null,
    sections,
    footer: {
      thanks: data.status === 'paid' ? 'Thank you, this invoice has been paid in full.' : 'Thank you for your business.',
      legalLine: legalLine(company),
      currencyLine: `All amounts in ${locale.currency}`,
    },
    formatMoney: (n) => formatMoney(n, locale),
    paper: locale.paper,
  };
}
